import { useDispatch } from "react-redux";
import { FC, useCallback, useContext, useEffect, useMemo } from "react";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ClientName, SliceStopCountFilter } from "@hopper-b2b/types";
import {
  getEnvVariables,
  useEnablePricePrediction,
} from "@hopper-b2b/utilities";
import { Box, Button, Chip } from "@material-ui/core";
import { ClientContext } from "../../../../../../App";
import { setRenderLoadingSteps } from "../../../../../shop/actions/actions";
import { useFetchFlights } from "../../../../../shop/hooks/useFetchFlights";
import { AppliedFilterTagsProps } from "./container";
import "./styles.scss";

const AppliedFilterTag: FC<{
  filter: string;
  onDelete: (filter: string) => void;
  deleteIcon?: any;
}> = ({ filter, onDelete, deleteIcon }) => {
  const { t } = useI18nContext();
  const handleDelete = useCallback(() => {
    onDelete(filter);
  }, [filter, onDelete]);

  const label = useMemo(() => {
    if (filter === "return" || filter === "departure") {
      return t(filter);
    }
    return t(`searchFilter.${filter}`);
  }, [filter, t]);

  const onKeyDownHandler = useCallback(
    (e) => {
      if (e.key === "Enter") {
        onDelete(filter);
      }
    },
    [filter, onDelete]
  );

  return (
    <Chip
      className={"applied-filter-chip"}
      label={label}
      aria-label={`Remove ${filter}`}
      onKeyDown={onKeyDownHandler}
      onDelete={handleDelete}
      color="secondary"
      {...(deleteIcon && {
        deleteIcon: <img src={deleteIcon} alt={`Remove ${filter}`} />,
      })}
    />
  );
};

export const AppliedFilterTags = (props: AppliedFilterTagsProps) => {
  const { filters, resets, setRerunPrediction, appliedStopsFilter } = props;
  const appliedFilters = useMemo(
    () => Object.keys(filters).filter((key) => filters[key]),
    [filters]
  );
  const dispatch = useDispatch();
  const enablePricePrediction = useEnablePricePrediction();
  const clientContext = useContext(ClientContext);
  const fetchFlights = useFetchFlights();

  const setLoadingState = useCallback(() => {
    dispatch(
      setRenderLoadingSteps(
        [ClientName.UBER, ClientName.NUBANK].includes(
          getEnvVariables("clientName") as ClientName
        )
      )
    );
  }, [dispatch]);

  const handleDelete = useCallback(
    (key: string) => {
      resets.reset[key]();
      if (key === "stops" && appliedStopsFilter === SliceStopCountFilter.NONE) {
        if (enablePricePrediction) {
          setRerunPrediction();
        }
        setLoadingState();
        fetchFlights();
      }
    },
    [
      appliedStopsFilter,
      enablePricePrediction,
      fetchFlights,
      resets.reset,
      setLoadingState,
      setRerunPrediction,
    ]
  );

  const onKeyDownHandler = useCallback(
    (e) => {
      if (e.key === "Enter") {
        if (
          appliedFilters.includes("stops") &&
          appliedStopsFilter === SliceStopCountFilter.NONE
        ) {
          if (enablePricePrediction) {
            setRerunPrediction();
          }
          setLoadingState();
          fetchFlights();
        }
        resets.resetAll();
      }
    },
    [
      appliedFilters,
      appliedStopsFilter,
      enablePricePrediction,
      fetchFlights,
      resets,
      setLoadingState,
      setRerunPrediction,
    ]
  );
  const onResetFilters = useCallback(() => {
    resets.resetAll();
    if (
      appliedFilters.includes("stops") &&
      appliedStopsFilter === SliceStopCountFilter.NONE
    ) {
      if (enablePricePrediction) {
        setRerunPrediction();
      }
      setLoadingState();
      fetchFlights();
    }
  }, [
    appliedFilters,
    appliedStopsFilter,
    enablePricePrediction,
    fetchFlights,
    resets,
    setLoadingState,
    setRerunPrediction,
  ]);

  const { t } = useI18nContext();

  if (appliedFilters.length <= 0) return null;
  return (
    <Box className={"applied-filter-tags"}>
      {appliedFilters.map((filter) => (
        <AppliedFilterTag
          key={filter}
          filter={filter}
          onDelete={handleDelete}
          deleteIcon={clientContext.assets?.deleteChip}
        />
      ))}
      {appliedFilters.length > 0 && (
        <Button
          className="reset-filters"
          onKeyDown={onKeyDownHandler}
          onClick={onResetFilters}
        >
          {t("resetFilters")}
        </Button>
      )}
    </Box>
  );
};
